// src/pages/LMS/Analytics/LMSAnalytics.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Button,
  CircularProgress, Divider, Stack, Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  School as CourseIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Assignment as TestIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axiosInstance from '../../../utils/axiosInstance';

const LMSAnalytics = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30_days');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Helper functions for safe data handling
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  
  const safeNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  const safeToFixed = (value, decimals = 1, defaultValue = 0) => {
    const num = safeNumber(value, defaultValue);
    return num.toFixed(decimals);
  };

  useEffect(() => {
    loadAnalytics();
  }, [period, chatbotId]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // UPDATED: New API endpoints
      // get_overall_analytics (renamed from get_lms_analytics)
      // get_performance_metrics (new)
      const [analyticsRes, metricsRes] = await Promise.all([
        axiosInstance.get('/api/lms/analytics', { 
          params: { period, chatbot_id: chatbotId } 
        }),
        axiosInstance.get('/api/lms/metrics', { 
          params: { chatbot_id: chatbotId } 
        })
      ]);

      console.log('Analytics data:', {
        analytics: analyticsRes.data,
        metrics: metricsRes.data
      });

      setAnalytics(analyticsRes.data.data || {});
      setMetrics(metricsRes.data.data || {});
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Note: export_analytics_report was removed, but we can use course/student exports
      // For now, we'll export overall analytics as a simple approach
      // You may want to create a custom export or use course-specific exports
      const response = await axiosInstance.get('/api/lms/analytics', {
        params: { period, chatbot_id: chatbotId, format: 'csv' },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lms-analytics-${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting analytics:', error);
      alert('Export functionality coming soon. Use course-specific exports instead.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
        >
          Back to LMS
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              LMS Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive insights into learning outcomes and engagement
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              label="Period"
            >
              <MenuItem value="7_days">Last 7 Days</MenuItem>
              <MenuItem value="30_days">Last 30 Days</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export Report
          </Button>
        </Stack>
      </Box>

      {/* Overview Cards - UPDATED: Using new analytics structure */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <CourseIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {safeNumber(analytics?.course_stats?.total_courses)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Courses
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="success.main">
                {safeNumber(analytics?.course_stats?.published_courses)} Published
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {safeNumber(analytics?.student_stats?.total_students)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Students
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {safeNumber(analytics?.student_stats?.new_students)} New
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <TrophyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {safeNumber(analytics?.enrollment_stats?.completed_enrollments)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completions
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="success.main">
                {safeToFixed(analytics?.enrollment_stats?.completion_rate)}% Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <TestIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {safeNumber(analytics?.enrollment_stats?.total_enrollments)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Enrollments
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {safeNumber(analytics?.enrollment_stats?.new_enrollments)} Recent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics - UPDATED: Using new metrics structure */}
      {metrics && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Key Performance Metrics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary.main" fontWeight={700}>
                  {safeNumber(metrics?.student_engagement?.students_with_activity)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Students
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {safeToFixed(metrics?.student_engagement?.engagement_rate)}% engagement
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="success.main" fontWeight={700}>
                  {safeToFixed(metrics?.course_completion?.completion_rate)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {safeNumber(metrics?.course_completion?.completed_enrollments)} completed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="warning.main" fontWeight={700}>
                  {safeToFixed(metrics?.test_performance?.pass_rate)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Test Pass Rate
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {safeNumber(metrics?.test_performance?.total_tests)} tests
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="info.main" fontWeight={700}>
                  {safeNumber(analytics?.engagement_stats?.certificates_issued)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Certificates Issued
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Popular Courses - UPDATED: Using popular_courses from analytics */}
      {safeArray(analytics?.popular_courses).length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top Courses by Enrollment
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={safeArray(analytics?.popular_courses)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="title" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="enrollment_count" fill="#8884d8" name="Enrollments" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Course Details Table */}
      {safeArray(analytics?.popular_courses).length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Course Performance Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course ID</TableCell>
                  <TableCell>Course Title</TableCell>
                  <TableCell align="right">Enrollments</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeArray(analytics?.popular_courses).map((course, index) => (
                  <TableRow key={course.course_id || index} hover>
                    <TableCell>
                      {/* NEW: Display course_identity */}
                      <Chip 
                        label={course.course_identity || course.course_id} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{course.title || 'Unknown'}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={safeNumber(course.enrollment_count)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(`/chatbot/${chatbotId}/lms/analytics/course/${course.course_identity || course.course_id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Student Stats Summary */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Student Statistics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Total Students</Typography>
                <Typography variant="h6">
                  {safeNumber(analytics?.student_stats?.total_students)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">New Students</Typography>
                <Typography variant="h6" color="success.main">
                  {safeNumber(analytics?.student_stats?.new_students)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Active Students</Typography>
                <Typography variant="h6" color="primary.main">
                  {safeNumber(analytics?.student_stats?.active_students)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Enrollment Statistics
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Total Enrollments</Typography>
                <Typography variant="h6">
                  {safeNumber(analytics?.enrollment_stats?.total_enrollments)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">New Enrollments</Typography>
                <Typography variant="h6" color="success.main">
                  {safeNumber(analytics?.enrollment_stats?.new_enrollments)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Completed</Typography>
                <Typography variant="h6" color="primary.main">
                  {safeNumber(analytics?.enrollment_stats?.completed_enrollments)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2">Completion Rate</Typography>
                <Typography variant="h6" color="warning.main">
                  {safeToFixed(analytics?.enrollment_stats?.completion_rate)}%
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LMSAnalytics;